import L from "leaflet";
import type { Observation, TrackPoint } from "./api";

// altitude color scale
function altColor(alt: number | null): string {
  if (alt === null) return "#888888";
  if (alt < 5000) return "#a6e3a1";   // green  — low
  if (alt < 15000) return "#f9e2af";  // yellow — mid
  return "#f38ba8";                    // red    — high
}

let _map: L.Map;
let _renderer: L.Canvas;
let _trackLayer: L.LayerGroup;
let _markerLayer: L.LayerGroup;
let _radiusCircle: L.Circle | null = null;
let _radiusMarker: L.CircleMarker | null = null;

type ClickCallback = (lat: number, lon: number) => void;
type MoveCallback = (lat: number, lon: number, zoom: number) => void;
let _onMapClick: ClickCallback | null = null;
let _onMapMove: MoveCallback | null = null;

export interface MapView { lat: number; lon: number; zoom: number; }

export function initMap(initialView?: MapView): L.Map {
  const center: [number, number] = initialView ? [initialView.lat, initialView.lon] : [39.5, -98.35];
  const zoom = initialView ? initialView.zoom : 5;
  _map = L.map("map").setView(center, zoom);

  L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    maxZoom: 19,
  }).addTo(_map);

  _trackLayer = L.layerGroup().addTo(_map);
  _markerLayer = L.layerGroup().addTo(_map);
  _renderer = L.canvas();

  _map.on("click", (e) => {
    if (_onMapClick) _onMapClick(e.latlng.lat, e.latlng.lng);
  });

  const emitMove = () => {
    if (_onMapMove) {
      const c = _map.getCenter();
      _onMapMove(c.lat, c.lng, _map.getZoom());
    }
  };
  _map.on("moveend", emitMove);
  _map.on("zoomend", emitMove);

  return _map;
}

export function onMapClick(cb: ClickCallback): void {
  _onMapClick = cb;
}

export function onMapMove(cb: MoveCallback): void {
  _onMapMove = cb;
}

export function getMapView(): MapView {
  const c = _map.getCenter();
  return { lat: c.lat, lon: c.lng, zoom: _map.getZoom() };
}

export function showRadiusCircle(lat: number, lon: number, radiusNm: number): void {
  if (_radiusCircle) _radiusCircle.remove();
  if (_radiusMarker) _radiusMarker.remove();
  const radiusM = radiusNm * 1852;
  _radiusCircle = L.circle([lat, lon], {
    radius: radiusM,
    color: "#89b4fa",
    fillColor: "#89b4fa",
    fillOpacity: 0.07,
    weight: 1.5,
  }).addTo(_map);
  _radiusMarker = L.circleMarker([lat, lon], {
    radius: 5,
    color: "#89b4fa",
    fillColor: "#89b4fa",
    fillOpacity: 0.9,
    renderer: _renderer,
  }).addTo(_map);
}

export function clearRadiusCircle(): void {
  if (_radiusCircle) { _radiusCircle.remove(); _radiusCircle = null; }
  if (_radiusMarker) { _radiusMarker.remove(); _radiusMarker = null; }
}

export function clearMarkers(): void {
  _markerLayer.clearLayers();
}

export function clearTrack(): void {
  _trackLayer.clearLayers();
}

export function plotObservations(
  observations: Observation[],
  onSelect: (obs: Observation) => void
): void {
  for (const obs of observations) {
    if (obs.lat === null || obs.lon === null) continue;
    const color = altColor(obs.alt_baro);
    const marker = L.circleMarker([obs.lat, obs.lon], {
      radius: 5,
      color,
      fillColor: color,
      fillOpacity: 0.85,
      weight: 1,
      renderer: _renderer,
    });
    const label = `
      <strong>${obs.flight ?? obs.hex}</strong><br/>
      Reg: ${obs.registration ?? "—"}<br/>
      Alt: ${obs.alt_baro != null ? obs.alt_baro + " ft" : "—"}<br/>
      GS: ${obs.ground_speed != null ? obs.ground_speed + " kt" : "—"}<br/>
      Squawk: ${obs.squawk ?? "—"}<br/>
      ${new Date(obs.observed_at).toISOString()}
    `;
    marker.bindPopup(label);
    marker.on("click", () => onSelect(obs));
    _markerLayer.addLayer(marker);
  }
}

export function showTrack(points: TrackPoint[], label: string): void {
  _trackLayer.clearLayers();
  if (!points.length) return;

  const latlngs = points.map((p) => [p.lat, p.lon] as [number, number]);
  const polyline = L.polyline(latlngs, { color: "#89b4fa", weight: 2 });
  _trackLayer.addLayer(polyline);

  // start marker
  L.circleMarker(latlngs[0], {
    radius: 7,
    color: "#a6e3a1",
    fillColor: "#a6e3a1",
    fillOpacity: 1,
    renderer: _renderer,
  })
    .bindPopup(`Start — ${label}`)
    .addTo(_trackLayer);

  // end marker
  L.circleMarker(latlngs[latlngs.length - 1], {
    radius: 7,
    color: "#f38ba8",
    fillColor: "#f38ba8",
    fillOpacity: 1,
    renderer: _renderer,
  })
    .bindPopup(`End — ${label}`)
    .addTo(_trackLayer);

  _map.fitBounds(polyline.getBounds(), { padding: [40, 40] });
}

export function getViewportCircle(): { lat: number; lon: number; radius_nm: number } {
  const center = _map.getCenter();
  const ne = _map.getBounds().getNorthEast();
  const R = 6371; // km
  const lat1 = (center.lat * Math.PI) / 180;
  const lat2 = (ne.lat * Math.PI) / 180;
  const dLat = ((ne.lat - center.lat) * Math.PI) / 180;
  const dLon = ((ne.lng - center.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  const distKm = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return { lat: center.lat, lon: center.lng, radius_nm: distKm / 1.852 };
}
